import React, { useState } from 'react';
import { MapPin, ChevronDown } from 'lucide-react';
import Reveal from './Reveal';

// ─────────────────────────────────────────────────────────────────────────────
// Service area — the suburbs I take work in, grouped geographically.
//
// SINGLE SOURCE OF TRUTH
//   SUBURB_GROUPS below is the only place suburbs live. Adding a suburb is a
//   one-line data change; the pill list, the "See all areas" expander and the
//   LocalBusiness JSON-LD (via SERVICE_SUBURBS) all follow.
//
// SEO RULE
//   Every suburb name must be in the rendered DOM regardless of the expander
//   state, so Google indexes them all. When collapsed, the "more" wrapper
//   only collapses visually (max-height / overflow-hidden). Do NOT swap this
//   for a conditional render — that hides the extra suburbs from crawlers.
//
// HONESTY RULE
//   Primrose Sands stays on the list because there's a real deck job in the
//   gallery there. Only add suburbs the owner is genuinely happy to drive to.
// ─────────────────────────────────────────────────────────────────────────────
export const SUBURB_GROUPS = [
  {
    key: 'hobart-inner',
    label: 'Hobart & Inner',
    suburbs: [
      'Hobart', 'North Hobart', 'West Hobart', 'South Hobart',
      'Battery Point', 'Sandy Bay', 'Dynnyrne', 'Mount Stuart',
      'Lenah Valley', 'New Town', 'Mount Nelson', 'Taroona',
    ],
  },
  {
    key: 'glenorchy-northern',
    label: 'Glenorchy & Northern Suburbs',
    suburbs: [
      'Glenorchy', 'Moonah', 'West Moonah', 'Derwent Park', 'Lutana',
      'Goodwood', 'Montrose', 'Rosetta', 'Berriedale', 'Chigwell',
      'Claremont', 'Austins Ferry',
    ],
  },
  {
    key: 'eastern-shore',
    label: 'Eastern Shore',
    suburbs: [
      'Bellerive', 'Rosny', 'Rosny Park', 'Howrah', 'Montagu Bay',
      'Warrane', 'Mornington', 'Lindisfarne', 'Geilston Bay',
      'Risdon Vale', 'Tranmere', 'Rokeby', 'Oakdowns', 'Cambridge',
      'Seven Mile Beach', 'Lauderdale',
    ],
  },
  {
    key: 'kingborough',
    label: 'South — Kingborough',
    suburbs: ['Kingston', 'Kingston Beach', 'Blackmans Bay', 'Bonnet Hill', 'Margate', 'Snug'],
  },
  {
    key: 'brighton-derwent',
    label: 'North — Brighton & Derwent Valley',
    suburbs: ['Old Beach', 'Brighton', 'Pontville', 'Bridgewater', 'Granton', 'New Norfolk'],
  },
  {
    key: 'sorell-coast',
    label: 'East — Sorell & Coast',
    suburbs: ['Sorell', 'Midway Point', 'Lewisham', 'Dodges Ferry', 'Primrose Sands', 'Dunalley'],
  },
  {
    // Bruny Island — its own dedicated group. The heading is a link to the
    // /carpenter-bruny-island landing page (rendered specially below).
    key: 'bruny-island',
    label: 'Bruny Island',
    href: '/carpenter-bruny-island',
    suburbs: [
      'Dennes Point', 'Barnes Bay', 'Great Bay', 'Alonnah',
      'Lunawanna', 'Adventure Bay', 'Simpsons Bay',
    ],
  },
];

// The first N groups render above the "See all areas" expander. Anything
// beyond stays in the DOM but is visually collapsed by default. Fifty-eight
// pills all at once looked heavy in a design review, so three groups (~40
// pills) surface immediately.
const VISIBLE_GROUP_COUNT = 3;

// Flat list of every serviced suburb — consumed by Footer.jsx for the
// LocalBusiness JSON-LD `areaServed` array. Derived from SUBURB_GROUPS so
// adding a suburb above is enough; no second list to keep in sync.
export const SERVICE_SUBURBS = SUBURB_GROUPS.flatMap((g) => g.suburbs);

const ServiceArea = () => {
  const [expanded, setExpanded] = useState(false);
  const visibleGroups = SUBURB_GROUPS.slice(0, VISIBLE_GROUP_COUNT);
  const hiddenGroups  = SUBURB_GROUPS.slice(VISIBLE_GROUP_COUNT);

  return (
    <section className="section area" id="areas" data-testid="area-section">
      <div className="container">
        <Reveal as="div" className="area-inner">
          <span className="area-icon" aria-hidden="true">
            <MapPin size={28} strokeWidth={1.8} />
          </span>
          <h2 className="section-h2">Serving Hobart and <span className="h-accent">surrounds</span></h2>
          <p className="area-text">
            Based in Hobart. Not on the list? Give me a call and ask &mdash; if it&rsquo;s
            nearby I&rsquo;ll usually still come out.
          </p>

          <div className="area-groups" data-testid="area-groups">
            {visibleGroups.map((group) => (
              <div key={group.key} className="area-group" data-testid={`area-group-${group.key}`}>
                <h3 className="area-group-label">{group.label}</h3>
                <ul className="area-suburb-list" aria-label={`Suburbs I service — ${group.label}`}>
                  {group.suburbs.map((s) => (
                    <li
                      key={s}
                      className="area-suburb-chip"
                      data-testid={`area-suburb-${s.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Collapsed wrapper. Renders ALL remaining suburbs into the DOM
                so Google indexes every name; the CSS only hides them
                visually until the button is pressed. */}
            {hiddenGroups.length > 0 && (
              <div
                className="area-more"
                data-open={expanded ? 'true' : 'false'}
                data-testid="area-more"
                aria-hidden={!expanded}
              >
                {hiddenGroups.map((group) => (
                  <div key={group.key} className="area-group" data-testid={`area-group-${group.key}`}>
                    <h3 className="area-group-label">
                      {group.href ? (
                        <a href={group.href} className="area-group-link" data-testid={`area-group-link-${group.key}`}>
                          {group.label}
                        </a>
                      ) : group.label}
                    </h3>
                    <ul className="area-suburb-list" aria-label={`Suburbs I service — ${group.label}`}>
                      {group.suburbs.map((s) => (
                        <li
                          key={s}
                          className="area-suburb-chip"
                          data-testid={`area-suburb-${s.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {hiddenGroups.length > 0 && (
            <button
              type="button"
              className="area-more-btn"
              data-testid="area-more-btn"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls="area-more"
            >
              <span>{expanded ? 'Show less' : `See all areas (${SERVICE_SUBURBS.length} suburbs)`}</span>
              <ChevronDown size={18} strokeWidth={2.2} aria-hidden="true" className={expanded ? 'is-open' : ''} />
            </button>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default ServiceArea;
